import {
  collection,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  query,
  serverTimestamp,
  setDoc,
  where,
} from 'firebase/firestore';
import { getDownloadURL, getStorage, ref, uploadBytes } from 'firebase/storage';
import { draftDocId, publishedDocId, versionedDocId } from './reportTemplates';

/**
 * Architect rules:
 * 1. Generate pins codes + templateVersion + PDF/ref on the history doc.
 *    History reads that snapshot — never current prose.
 * 2. DA comes from the admin-area scorer (cloud: Normalized/sd; legacy:
 *    RTDB chart compare). This module does not score.
 * 3. Drafts are mutable. Publishing creates {sex}_v{n}. Never overwrite a
 *    published version doc. {sex}_published is only a latest-version pointer.
 * 4. vasi_functions seed is insert-if-missing. Admin fills gaps with
 *    draft → publish (a new version).
 * Templates are shared. History collections are per area.
 */
export const TEMPLATES_COLLECTION = 'interpretation_templates';
export const REPORTS_COLLECTION = 'cloud_eval_reports';
export const LEGACY_REPORTS_COLLECTION = 'legacy_eval_reports';

export const REPORTS_COLLECTIONS = {
  cloud: REPORTS_COLLECTION,
  legacy: LEGACY_REPORTS_COLLECTION,
};

export function reportsCollectionFor(source = 'cloud') {
  return REPORTS_COLLECTIONS[source] ?? REPORTS_COLLECTION;
}

function asTemplate(snapshot, fallbackId) {
  if (!snapshot?.exists?.()) return null;
  return { id: snapshot.id ?? fallbackId, ...snapshot.data() };
}

export function assertPinnedReportPayload({ codes, templateVersion, pdfPath } = {}) {
  if (!codes || typeof codes !== 'object') {
    throw new Error('Generate must persist codes on the history document.');
  }
  if (!Number.isFinite(templateVersion) || templateVersion < 1) {
    throw new Error('Generate must pin a published template version on the history document.');
  }
  if (!pdfPath) {
    throw new Error('Generate must persist a PDF path or blob ref on the history document.');
  }
}

export async function loadTemplatesForSex(sex, { db = getFirestore() } = {}) {
  if (!sex) {
    return { published: null, draft: null };
  }

  const [publishedSnap, draftSnap] = await Promise.all([
    getDoc(doc(db, TEMPLATES_COLLECTION, publishedDocId(sex))),
    getDoc(doc(db, TEMPLATES_COLLECTION, draftDocId(sex))),
  ]);

  return {
    published: asTemplate(publishedSnap, publishedDocId(sex)),
    draft: asTemplate(draftSnap, draftDocId(sex)),
  };
}

export async function loadPublishedVersion(sex, version, { db = getFirestore() } = {}) {
  if (!sex || !Number.isFinite(Number(version))) return null;
  const numericVersion = Number(version);
  const versioned = asTemplate(
    await getDoc(doc(db, TEMPLATES_COLLECTION, versionedDocId(sex, numericVersion))),
    versionedDocId(sex, numericVersion)
  );
  if (versioned) return versioned;

  const pointer = asTemplate(
    await getDoc(doc(db, TEMPLATES_COLLECTION, publishedDocId(sex))),
    publishedDocId(sex)
  );
  if (pointer?.version === numericVersion) return pointer;
  return null;
}

export async function saveDraft({ sex, keys, userId, db = getFirestore() } = {}) {
  const publishedSnap = await getDoc(doc(db, TEMPLATES_COLLECTION, publishedDocId(sex)));
  const published = publishedSnap.exists() ? publishedSnap.data() : null;
  const payload = {
    sex,
    status: 'draft',
    version: published?.version ?? 0,
    keys: keys ?? {},
    updatedAt: serverTimestamp(),
    updatedBy: userId ?? null,
  };
  await setDoc(doc(db, TEMPLATES_COLLECTION, draftDocId(sex)), payload, { merge: true });
  return payload;
}

export async function publishDraft({ sex, userId, db = getFirestore() } = {}) {
  const [draftSnap, publishedSnap] = await Promise.all([
    getDoc(doc(db, TEMPLATES_COLLECTION, draftDocId(sex))),
    getDoc(doc(db, TEMPLATES_COLLECTION, publishedDocId(sex))),
  ]);

  const draft = draftSnap.exists() ? draftSnap.data() : null;
  if (!draft) {
    throw new Error('No draft to publish. Save a draft first.');
  }

  const nextVersion = (publishedSnap.exists() ? publishedSnap.data().version : 0) + 1;
  const versionId = versionedDocId(sex, nextVersion);
  const existingVersion = await getDoc(doc(db, TEMPLATES_COLLECTION, versionId));
  if (existingVersion.exists()) {
    throw new Error(`Published version ${sex} v${nextVersion} already exists and cannot be overwritten.`);
  }

  const payload = {
    sex,
    status: 'published',
    version: nextVersion,
    keys: draft.keys ?? {},
    createdAt: serverTimestamp(),
    publishedAt: serverTimestamp(),
    publishedBy: userId ?? null,
    updatedAt: serverTimestamp(),
  };

  await setDoc(doc(db, TEMPLATES_COLLECTION, versionId), payload);
  await setDoc(doc(db, TEMPLATES_COLLECTION, publishedDocId(sex)), {
    sex,
    status: 'published',
    version: nextVersion,
    versionId,
    keys: payload.keys,
    publishedAt: payload.publishedAt,
    publishedBy: userId ?? null,
    updatedAt: payload.updatedAt,
  });
  await setDoc(doc(db, TEMPLATES_COLLECTION, draftDocId(sex)), {
    ...draft,
    version: nextVersion,
    updatedAt: serverTimestamp(),
  }, { merge: true });

  return payload;
}

export async function listReports({
  evaluationId,
  clientId,
  source = 'cloud',
  db = getFirestore(),
} = {}) {
  if (!evaluationId) return [];
  const snapshot = await getDocs(
    query(collection(db, reportsCollectionFor(source)), where('evaluationId', '==', evaluationId))
  );
  return snapshot.docs
    .map((reportDoc) => ({ id: reportDoc.id, ...reportDoc.data() }))
    .filter((report) => !clientId || report.clientId === clientId)
    .sort((left, right) => {
      const leftTime = left.createdAt?.toMillis?.() ?? Date.parse(left.createdAt ?? 0) ?? 0;
      const rightTime = right.createdAt?.toMillis?.() ?? Date.parse(right.createdAt ?? 0) ?? 0;
      return rightTime - leftTime;
    });
}

export async function saveGeneratedReport({
  clientId,
  evaluationId,
  organizationId,
  clientName,
  sex,
  codes,
  templateVersion,
  templateId,
  pdfBlob,
  userId,
  source = 'cloud',
  db = getFirestore(),
  storage = getStorage(),
} = {}) {
  const collectionName = reportsCollectionFor(source);
  const reportRef = doc(collection(db, collectionName));
  const pdfPath = `${collectionName}/${clientId}/${evaluationId}/${reportRef.id}.pdf`;
  assertPinnedReportPayload({ codes, templateVersion, pdfPath });

  let pdfUrl = null;
  try {
    await uploadBytes(ref(storage, pdfPath), pdfBlob, { contentType: 'application/pdf' });
    pdfUrl = await getDownloadURL(ref(storage, pdfPath));
  } catch (error) {
    console.error('Eval report PDF upload failed; history will store a local-only generation:', error);
  }

  const payload = {
    clientId,
    evaluationId,
    organizationId: organizationId ?? null,
    clientName: clientName ?? null,
    sex,
    source,
    codes,
    templateVersion,
    templateId: templateId ?? versionedDocId(sex, templateVersion),
    pdfPath,
    pdfUrl,
    createdAt: serverTimestamp(),
    createdBy: userId ?? null,
  };

  await setDoc(reportRef, payload);
  return { id: reportRef.id, ...payload };
}

export function createFirestoreReportApi(source = 'cloud') {
  return {
    loadTemplatesForSex,
    loadPublishedVersion,
    saveDraft,
    publishDraft,
    listReports: (args) => listReports({ ...args, source }),
    saveGeneratedReport: (args) => saveGeneratedReport({ ...args, source }),
  };
}

export const firestoreReportApi = createFirestoreReportApi('cloud');
export const firestoreLegacyReportApi = createFirestoreReportApi('legacy');
